import { useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency } from '../../utils/formatters'
import { camposDeProductoModificados, describirCambios } from '../../utils/planProducto'
import CampanasInvitados from './CampanasInvitados'

/**
 * Configuración comercial: planes, licencias y la bolsa de timbres.
 *
 * Sólo el superadministrador. El menú no la muestra a nadie más, pero **el control real está
 * en el servidor** (`licencias_api.EsSuperAdmin`): ocultar un botón no es control de acceso.
 */
export default function Licenciamiento() {
  const { user, isSuperuser } = useAuth()
  const queryClient = useQueryClient()
  const [modal, setModal] = useState(null)        // 'contador' | 'plan' | 'asignar' | 'saldo'
  const [editando, setEditando] = useState(null)  // el contador que se edita, o null al crear
  const [cambiando, setCambiando] = useState(null) // la licencia a la que se le cambia el plan
  const [planEditado, setPlanEditado] = useState(null) // el plan que se ve o edita, o null al crear

  const esSuper = isSuperuser || user?.role === 'superadmin'

  const { data: bolsa, isLoading: cargandoBolsa } = useQuery(
    'bolsa-timbres',
    async () => (await api.get('/auth/bolsa-timbres/')).data,
    { enabled: esSuper }
  )
  const { data: paquetes = [] } = useQuery(
    'paquetes-comerciales',
    async () => {
      const r = await api.get('/auth/paquetes/')
      return r.data.results || r.data || []
    },
    { enabled: esSuper }
  )
  const { data: licencias = [] } = useQuery(
    'licencias',
    async () => {
      const r = await api.get('/auth/licencias/')
      return r.data.results || r.data || []
    },
    { enabled: esSuper }
  )

  const { data: contadores = [] } = useQuery(
    'contadores',
    async () => {
      const r = await api.get('/auth/contadores/')
      return r.data.results || r.data || []
    },
    { enabled: esSuper }
  )

  const refrescar = () => {
    queryClient.invalidateQueries('bolsa-timbres')
    queryClient.invalidateQueries('paquetes-comerciales')
    queryClient.invalidateQueries('licencias')
    queryClient.invalidateQueries('contadores')
  }

  if (!esSuper) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          Esta sección es exclusiva del superadministrador.
        </Alert>
      </Container>
    )
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-1">Licenciamiento</h2>
      <p className="text-muted">
        Planes comerciales, licencias de los contadores, bolsa de timbres y campañas de invitados.
      </p>

      <BolsaTimbres bolsa={bolsa} cargando={cargandoBolsa} onRegistrar={() => setModal('saldo')} />

      {/* Los contadores van primero: son la unidad comercial y su licencia es un atributo
          suyo. Antes nacían en el Django Admin y había que volver aquí a darles plan. */}
      <Contadores contadores={contadores} onNuevo={() => setModal('contador')}
                  onEditar={(c) => { setEditando(c); setModal('contador') }} />

      <Row className="mt-4">
        <Col lg={6} className="mb-4">
          <Paquetes paquetes={paquetes}
                    onNuevo={() => { setPlanEditado(null); setModal('plan') }}
                    onEditar={(p) => { setPlanEditado(p); setModal('plan') }} />
        </Col>
        <Col lg={6} className="mb-4">
          <Licencias licencias={licencias} onAsignar={() => setModal('asignar')}
                     onCambiar={(l) => { setCambiando(l); setModal('cambiar') }}
                     onAmpliar={(l) => { setCambiando(l); setModal('ampliar') }} />
        </Col>
      </Row>

      {/* Las campañas del registro público (las tarjetas con QR). Antes sólo vivían en el Django
          Admin y era fácil olvidar que estaban abiertas. */}
      <CampanasInvitados paquetes={paquetes} />

      {modal === 'contador' && (
        <FormContador paquetes={paquetes} contador={editando}
                      onHide={() => { setModal(null); setEditando(null) }}
                      onSuccess={() => { setModal(null); setEditando(null); refrescar() }} />
      )}
      {modal === 'cambiar' && cambiando && (
        <FormCambioPlan licencia={cambiando} paquetes={paquetes}
                        onHide={() => { setModal(null); setCambiando(null) }}
                        onSuccess={() => { setModal(null); setCambiando(null); refrescar() }} />
      )}
      {modal === 'ampliar' && cambiando && (
        <FormAmpliar licencia={cambiando}
                     onHide={() => { setModal(null); setCambiando(null) }}
                     onSuccess={() => { setModal(null); setCambiando(null); refrescar() }} />
      )}
      {modal === 'plan' && (
        <FormPlan plan={planEditado}
                  onHide={() => { setModal(null); setPlanEditado(null) }}
                  onSuccess={() => { setModal(null); setPlanEditado(null); refrescar() }} />
      )}
      {modal === 'asignar' && (
        <FormAsignar paquetes={paquetes} onHide={() => setModal(null)}
                     onSuccess={() => { setModal(null); refrescar() }} />
      )}
      {modal === 'saldo' && (
        <FormSaldo bolsa={bolsa} onHide={() => setModal(null)}
                   onSuccess={() => { setModal(null); refrescar() }} />
      )}
    </Container>
  )
}

function BolsaTimbres({ bolsa, cargando, onRegistrar }) {
  if (cargando) return <Card body><Spinner size="sm" /> Cargando la bolsa…</Card>
  if (!bolsa?.disponible) {
    return (
      <Alert variant="warning" className="d-flex justify-content-between align-items-center">
        <span>
          No se pudo leer el saldo de timbres: ni la consulta al PAC ni el último comprobante
          dieron un dato. Si acabas de comprar, regístralo a mano.
        </span>
        <Button size="sm" onClick={onRegistrar}>Registrar saldo</Button>
      </Alert>
    )
  }

  const libres = bolsa.libres ?? 0
  const variante = libres <= 0 ? 'danger' : libres < 100 ? 'warning' : 'success'
  const conciliacion = bolsa.conciliacion

  return (
    <Card border={variante === 'success' ? undefined : variante}>
      <Card.Body>
        {/* La sobreventa va arriba de todo: si prometimos más folios de los que hay, el contador
            que timbre último verá «no quedan timbres» y creerá que el problema es suyo. */}
        {conciliacion?.estado === 'sobreventa' && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-octagon-fill"></i>
            <span>{conciliacion.mensaje}</span>
          </Alert>
        )}
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h5 className="mb-1">Bolsa de timbres</h5>
            <div className="text-muted small">
              {/* El origen dice de dónde salió: una consulta al PAC, el folio de un
                  comprobante o una captura. No es lo mismo y conviene que se vea. */}
              Último corte: {bolsa.fecha_corte} · {bolsa.origen}
            </div>
          </div>
          <div className="d-flex gap-4 text-center">
            <div>
              <div className="h4 mb-0">{bolsa.saldo}</div>
              <div className="small text-muted">en la cuenta maestra</div>
            </div>
            <div>
              <div className="h4 mb-0">{bolsa.comprometidos}</div>
              <div className="small text-muted">prometidos a contadores</div>
            </div>
            <div>
              <div className={`h4 mb-0 text-${variante}`}>{libres}</div>
              <div className="small text-muted">libres para vender</div>
            </div>
          </div>
          <Button variant="outline-primary" onClick={onRegistrar}>
            Registrar compra de timbres
          </Button>
        </div>

        {libres < 500 && (
          <Alert variant={variante} className="mt-3 mb-0 py-2">
            {libres <= 0
              ? 'No quedan timbres libres: no se puede dar de alta ningún contador nuevo hasta surtir la bolsa.'
              : `Quedan ${libres} timbres libres. Un plan de 500 ya no cabe: surte la bolsa antes de vender uno.`}
          </Alert>
        )}
      </Card.Body>
    </Card>
  )
}

function Contadores({ contadores, onNuevo, onEditar }) {
  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Contadores</strong>
        <Button size="sm" onClick={onNuevo}>+ Nuevo contador</Button>
      </Card.Header>
      <Card.Body className="p-0">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Usuario</th><th>Correo</th><th>Rol</th><th>Plan</th>
              <th>RFC</th><th>Timbres</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {contadores.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-4">
                Sin contadores todavía.
              </td></tr>
            )}
            {contadores.map((c) => {
              const lic = c.licencia
              return (
                <tr key={c.id}>
                  <td><strong>{c.username}</strong>{c.first_name ? ` — ${c.first_name}` : ''}</td>
                  <td className="small text-muted">{c.email || '—'}</td>
                  <td><Badge bg="secondary">{c.role}</Badge></td>
                  <td>{lic ? lic.plan : <span className="text-muted">sin plan</span>}</td>
                  <td>
                    {lic
                      ? (lic.capacidad.ilimitada
                          ? <Badge bg="info">ilimitado</Badge>
                          : `${lic.capacidad.usados} de ${lic.capacidad.contratada}`)
                      : '—'}
                  </td>
                  <td>
                    {/* Un plan interno no consume de ninguna bolsa vendida: mostrar «0 de 0»
                        haría pensar que se quedó sin timbres. */}
                    {lic && lic.timbres.otorgados > 0
                      ? `${lic.timbres.disponibles} de ${lic.timbres.otorgados}`
                      : '—'}
                  </td>
                  <td>
                    {!c.is_active
                      ? <Badge bg="danger">inactivo</Badge>
                      : lic && !lic.vigente
                        ? <Badge bg="warning" text="dark">vigencia vencida</Badge>
                        : <Badge bg="success">activo</Badge>}
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" onClick={() => onEditar(c)}>
                      Editar
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  )
}


function Paquetes({ paquetes, onNuevo, onEditar }) {
  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Planes</strong>
        <Button size="sm" onClick={onNuevo}>+ Nuevo plan</Button>
      </Card.Header>
      <Table responsive hover className="mb-0">
        <thead>
          <tr>
            <th>Plan</th><th>Capacidad</th><th>Timbres</th><th>Precio</th><th>Licencias</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {paquetes.length === 0 && (
            <tr><td colSpan={6} className="text-center text-muted py-4">
              Todavía no hay planes. Crea el primero para poder vender.
            </td></tr>
          )}
          {paquetes.map(p => (
            <tr key={p.id}>
              <td>
                {p.nombre}{' '}
                <span className="text-muted small">v{p.version}</span>{' '}
                {p.es_interno && <Badge bg="secondary">interno</Badge>}
                {!p.activo && <Badge bg="light" text="dark">inactivo</Badge>}
              </td>
              <td>{p.capacidad_texto}<div className="small text-muted">
                +{p.reemplazos_incluidos} reemplazos</div></td>
              <td>{p.timbres_incluidos}</td>
              <td>{formatCurrency(p.precio)}</td>
              <td>{p.licencias_activas}</td>
              <td className="text-end">
                <Button size="sm" variant="outline-secondary" onClick={() => onEditar(p)}>
                  Ver / editar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

function Licencias({ licencias, onAsignar, onCambiar, onAmpliar }) {
  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Licencias</strong>
        <Button size="sm" onClick={onAsignar}>+ Asignar plan</Button>
      </Card.Header>
      <Table responsive hover className="mb-0">
        <thead>
          <tr><th>Contador</th><th>Plan</th><th>Uso</th><th>Timbres</th><th>Estado</th><th></th></tr>
        </thead>
        <tbody>
          {licencias.length === 0 && (
            <tr><td colSpan={6} className="text-center text-muted py-4">
              Ninguna licencia asignada.
            </td></tr>
          )}
          {licencias.map(l => (
            <tr key={l.id}>
              <td>{l.contador_username}</td>
              <td>{l.paquete_nombre}<div className="small text-muted">{l.capacidad_texto}</div></td>
              <td>
                {l.uso?.activos} activos
                {l.uso?.cupo_permitidos != null && (
                  <div className="small text-muted">
                    {l.uso.distintos} de {l.uso.cupo_permitidos} distintos
                  </div>
                )}
              </td>
              <td>{l.timbres_disponibles} de {l.timbres_otorgados}</td>
              <td><Badge bg={l.estado === 'activa' ? 'success' : 'secondary'}>{l.estado}</Badge></td>
              <td className="text-end">
                {/* Un contador con licencia no puede recibir otra --`contador` es OneToOne-- así
                    que cambiar de plan se hace aquí, sobre la licencia que ya tiene. */}
                <Button size="sm" variant="outline-primary" onClick={() => onCambiar(l)}>
                  Cambiar plan
                </Button>
                {/* Ampliar es crecer **sin** cambiar de plan: sólo aparece si el plan lo admite
                    y si la capacidad no es ilimitada, porque ahí no hay nada que ampliar. */}
                {l.condiciones?.permite_bloques && l.capacidad_rfc_total != null && (
                  <Button size="sm" variant="outline-secondary" className="ms-2"
                          onClick={() => onAmpliar(l)}>
                    Ampliar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

/**
 * Ampliar con bloques: crecer **sin** cambiar de plan.
 *
 * El modal dice en qué queda la licencia antes de confirmar --de 5 a 10 RFC, de 500 a 600
 * timbres, $500--, porque «ampliar» sin número no permite decidir.
 */
function FormAmpliar({ licencia, onHide, onSuccess }) {
  const [cantidad, setCantidad] = useState(1)
  const [errores, setErrores] = useState({})

  const cond = licencia.condiciones || {}
  const bloques = Math.max(1, parseInt(cantidad, 10) || 1)
  const rfcPorBloque = Number(cond.tamano_bloque || 0)
  const timbresPorBloque = Number(cond.timbres_bloque || 0)
  const precio = Number(cond.precio_bloque || 0)

  const capacidadNueva = (licencia.capacidad_rfc_total || 0) + rfcPorBloque * bloques
  const timbresNuevos = (licencia.timbres_otorgados || 0) + timbresPorBloque * bloques

  const ampliar = useMutation(
    async () => (await api.post(`/auth/licencias/${licencia.id}/ampliar/`,
                                { cantidad: bloques })).data,
    { onSuccess, onError: (e) => setErrores(e.response?.data || {}) }
  )

  const mensajeError = errores.bloques || errores.bolsa || errores.capacidad || errores.cantidad

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Ampliar la licencia de {licencia.contador_username}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {mensajeError && <Alert variant="danger">{mensajeError}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Bloques</Form.Label>
          <Form.Control type="number" min={1} value={cantidad}
                        onChange={(e) => setCantidad(e.target.value)} />
          <Form.Text>
            Cada bloque entrega {rfcPorBloque} RFC
            {timbresPorBloque > 0 && ` y ${timbresPorBloque} timbres`}.
          </Form.Text>
        </Form.Group>

        {/* En qué queda: sin esto, «ampliar» es un botón a ciegas. */}
        <Table size="sm" className="mb-0">
          <tbody>
            <tr>
              <td className="text-muted">Capacidad</td>
              <td className="text-end">
                {licencia.capacidad_rfc_total} → <strong>{capacidadNueva}</strong> RFC
              </td>
            </tr>
            {timbresPorBloque > 0 && (
              <tr>
                <td className="text-muted">Timbres otorgados</td>
                <td className="text-end">
                  {licencia.timbres_otorgados} → <strong>{timbresNuevos}</strong>
                </td>
              </tr>
            )}
            <tr>
              <td className="text-muted">Precio</td>
              <td className="text-end">
                <strong>${(precio * bloques).toFixed(2)}</strong>
                {bloques > 1 && (
                  <span className="text-muted small"> ({bloques} × ${precio.toFixed(2)})</span>
                )}
              </td>
            </tr>
          </tbody>
        </Table>
        <div className="small text-muted mt-2">
          Los timbres se <strong>suman</strong> a los que ya tiene, y su cupo de rotación crece
          con la capacidad.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button onClick={() => { setErrores({}); ampliar.mutate() }}
                disabled={ampliar.isLoading}>
          {ampliar.isLoading ? 'Ampliando…' : 'Ampliar'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function FormCambioPlan({ licencia, paquetes, onHide, onSuccess }) {
  const [paquete, setPaquete] = useState('')
  const [errores, setErrores] = useState({})

  const cambiar = useMutation(
    async () => (await api.post(`/auth/licencias/${licencia.id}/cambiar_paquete/`,
                                { paquete })).data,
    {
      onSuccess: () => { toast.success('Plan cambiado'); onSuccess() },
      onError: (e) => {
        const d = e.response?.data || {}
        setErrores(d)
        // La bolsa y la capacidad son razones de negocio, no errores de captura: se dicen solas.
        if (d.bolsa) toast.error(String(d.bolsa))
        else if (d.capacidad) toast.error(String(d.capacidad))
        else toast.error('No se pudo cambiar el plan')
      },
    }
  )

  const destino = paquetes.find((p) => String(p.id) === String(paquete))
  const mismo = destino && destino.nombre === licencia.paquete_nombre

  return (
    <Modal show onHide={onHide} backdrop="static">
      <Form onSubmit={(e) => { e.preventDefault(); setErrores({}); cambiar.mutate() }}>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar el plan de {licencia.contador_username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <dl className="row small mb-3">
            <dt className="col-5">Plan actual</dt>
            <dd className="col-7">{licencia.paquete_nombre}</dd>
            <dt className="col-5">Timbres</dt>
            <dd className="col-7">{licencia.timbres_disponibles} de {licencia.timbres_otorgados}</dd>
            <dt className="col-5">Contribuyentes activos</dt>
            <dd className="col-7">{licencia.uso?.activos ?? '—'}</dd>
          </dl>

          <Form.Group className="mb-3">
            <Form.Label>Plan nuevo *</Form.Label>
            <Form.Select name="paquete_destino" value={paquete}
                         onChange={(e) => setPaquete(e.target.value)} required>
              <option value="">Elegir…</option>
              {paquetes.filter((p) => p.activo && p.nombre !== licencia.paquete_nombre).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} — {p.capacidad_texto} — {p.timbres_incluidos} timbres
                </option>
              ))}
            </Form.Select>
            {errores.paquete && (
              <Form.Text className="text-danger d-block">{String(errores.paquete)}</Form.Text>
            )}
          </Form.Group>

          {destino && !mismo && (
            <Alert variant="info" className="mb-0">
              {/* Lo acordado con el dueño: el saldo no gastado sigue siendo suyo. Decirlo aquí
                  evita la duda de si el cambio le borra los timbres que pagó. */}
              Los timbres <strong>se suman</strong>: quedará con{' '}
              <strong>{(licencia.timbres_otorgados || 0) + (destino.timbres_incluidos || 0)}</strong>
              {' '}otorgados y conservará los {licencia.timbres_consumidos ?? 0} ya usados.
              La vigencia se recorre desde hoy.
            </Alert>
          )}

          {errores.capacidad && <Alert variant="danger" className="mt-3">{String(errores.capacidad)}</Alert>}
          {errores.bolsa && <Alert variant="danger" className="mt-3">{String(errores.bolsa)}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={!paquete || cambiar.isLoading}>
            {cambiar.isLoading ? 'Cambiando…' : 'Cambiar plan'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}


function FormContador({ paquetes, contador, onHide, onSuccess }) {
  const editando = !!contador
  const [pestana, setPestana] = useState('datos')
  const [datos, setDatos] = useState({
    username: contador?.username || '',
    email: contador?.email || '',
    first_name: contador?.first_name || '',
    last_name: contador?.last_name || '',
    role: contador?.role || 'contador',
    is_active: contador?.is_active ?? true,
    password: '',
    paquete: '',
  })
  const [errores, setErrores] = useState({})

  const guardar = useMutation(
    async (cuerpo) => editando
      ? (await api.patch(`/auth/contadores/${contador.id}/`, cuerpo)).data
      : (await api.post('/auth/contadores/', cuerpo)).data,
    {
      onSuccess: () => {
        toast.success(editando ? 'Contador actualizado' : 'Contador creado')
        onSuccess()
      },
      onError: (e) => {
        const d = e.response?.data || {}
        setErrores(d)
        // La bolsa insuficiente es la razón de negocio, no un error de captura: se dice sola
        // para que no se pierda entre los campos.
        if (d.bolsa) toast.error(String(d.bolsa))
        else toast.error('No se pudo guardar')
      },
    }
  )

  const enviar = (e) => {
    e.preventDefault()
    setErrores({})
    const cuerpo = { ...datos }
    if (!cuerpo.paquete) delete cuerpo.paquete
    if (editando) {
      // Al editar, el plan no se toca por aquí --sería una licencia nueva-- y una contraseña
      // vacía significa «déjala como está».
      delete cuerpo.paquete
      if (!cuerpo.password) delete cuerpo.password
      delete cuerpo.username
    }
    guardar.mutate(cuerpo)
  }

  const err = (campo) => errores[campo] && (
    <Form.Text className="text-danger d-block">{String(errores[campo])}</Form.Text>
  )

  const plan = paquetes.find((p) => String(p.id) === String(datos.paquete))

  return (
    <Modal show onHide={onHide} size="lg" backdrop="static">
      <Form onSubmit={enviar}>
        <Modal.Header closeButton>
          <Modal.Title>{editando ? `Editar ${contador.username}` : 'Nuevo contador'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={pestana} onSelect={(k) => setPestana(k)} className="mb-3">
            <Tab eventKey="datos" title="Datos del contador">
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Usuario *</Form.Label>
                  <Form.Control name="username" value={datos.username} disabled={editando}
                    required
                    onChange={(e) => setDatos({ ...datos, username: e.target.value })} />
                  {editando && (
                    <Form.Text className="text-muted">
                      El usuario no se cambia: es con lo que inicia sesión.
                    </Form.Text>
                  )}
                  {err('username')}
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Correo</Form.Label>
                  <Form.Control type="email" name="email" value={datos.email}
                    onChange={(e) => setDatos({ ...datos, email: e.target.value })} />
                  {err('email')}
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control name="first_name" value={datos.first_name}
                    onChange={(e) => setDatos({ ...datos, first_name: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Apellido</Form.Label>
                  <Form.Control name="last_name" value={datos.last_name}
                    onChange={(e) => setDatos({ ...datos, last_name: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Rol *</Form.Label>
                  <Form.Select name="role" value={datos.role}
                    onChange={(e) => setDatos({ ...datos, role: e.target.value })}>
                    <option value="contador">contador</option>
                    <option value="admin">admin</option>
                    <option value="cliente">cliente</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    El rol define qué clase de usuario es. Lo que puede ver lo decide su plan,
                    no el rol.
                  </Form.Text>
                  {err('role')}
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>{editando ? 'Nueva contraseña' : 'Contraseña *'}</Form.Label>
                  <Form.Control type="password" name="password" value={datos.password}
                    autoComplete="new-password" required={!editando}
                    onChange={(e) => setDatos({ ...datos, password: e.target.value })} />
                  {editando && (
                    <Form.Text className="text-muted">Vacío = no la cambies.</Form.Text>
                  )}
                  {err('password')}
                </Col>
                {editando && (
                  <Col md={12} className="mb-2">
                    <Form.Check type="switch" label="Activo" checked={datos.is_active}
                      onChange={(e) => setDatos({ ...datos, is_active: e.target.checked })} />
                    <Form.Text className="text-muted">
                      Un contador no se borra: se desactiva. Sus timbres sin usar vuelven a la
                      bolsa y se le regresan al reactivarlo, si la bolsa alcanza.
                    </Form.Text>
                    {errores.bolsa && (
                      <Alert variant="danger" className="mt-2 mb-0">{String(errores.bolsa)}</Alert>
                    )}
                  </Col>
                )}
              </Row>
            </Tab>

            <Tab eventKey="licencia" title="Licencia">
              {editando ? (
                <Alert variant="secondary" className="mb-0">
                  {contador.licencia
                    ? <>Plan actual: <strong>{contador.licencia.plan}</strong>. Para cambiarlo,
                       usa <em>Cambiar plan</em> en la tabla de <em>Licencias</em>: los timbres se
                       suman a los que ya tiene y queda registrado el movimiento.</>
                    : <>Sin plan. Se le asigna desde <em>Licencias</em>.</>}
                </Alert>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Plan</Form.Label>
                    <Form.Select name="paquete" value={datos.paquete}
                      onChange={(e) => setDatos({ ...datos, paquete: e.target.value })}>
                      <option value="">Sin plan por ahora</option>
                      {paquetes.filter((p) => p.activo).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} — {p.capacidad_texto} — {p.timbres_incluidos} timbres
                        </option>
                      ))}
                    </Form.Select>
                    {err('paquete')}
                  </Form.Group>

                  {plan && (
                    <Alert variant="info">
                      Se le otorgan <strong>{plan.timbres_incluidos} timbres</strong> y
                      {' '}{plan.capacidad_texto}. Si la bolsa maestra no alcanza, el alta se
                      rechaza completa — no queda un contador a medias.
                    </Alert>
                  )}
                  {errores.bolsa && <Alert variant="danger">{String(errores.bolsa)}</Alert>}
                </>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={guardar.isLoading}>
            {guardar.isLoading ? 'Guardando…' : editando ? 'Guardar' : 'Crear contador'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}


function FormPlan({ plan, onHide, onSuccess }) {
  const editando = Boolean(plan?.id)
  const [datos, setDatos] = useState(() => ({
    nombre: '', descripcion: '', activo: true, es_interno: false,
    capacidad_rfc: 5, reemplazos_incluidos: 5, timbres_incluidos: 500,
    precio: '1750.00', max_usuarios: 1, vigencia_dias: 365,
    dias_retencion: 30, max_sustituciones_slot: 2,
    modulos: ['facturacion'], complementos: ['rep'],
    // Bloques: la vía para que el contador crezca sin cambiar de plan. Apagados por defecto,
    // porque un plan que no los vende es lo normal.
    permite_bloques: false, tamano_bloque: 5, precio_bloque: '500.00', timbres_bloque: 100,
    ...(plan || {}),
  }))
  const [errores, setErrores] = useState({})
  const [confirmando, setConfirmando] = useState(null)

  // Qué campos de producto cambió el usuario respecto al plan que abrió.
  const cambiosDeProducto = editando ? camposDeProductoModificados(plan, datos) : []

  // El catálogo viene del backend, no de una lista aquí: si se duplicara, un módulo nuevo
  // quedaría invisible al crear planes --sin error, sin aviso-- y nadie podría venderlo.
  const { data: catalogo } = useQuery(
    'catalogo-modulos',
    async () => (await api.get('/auth/paquetes/catalogo_modulos/')).data
  )

  const alternarModulo = (clave) => setDatos((d) => ({
    ...d,
    modulos: d.modulos.includes(clave)
      ? d.modulos.filter((m) => m !== clave)
      : [...d.modulos, clave],
  }))

  const crear = useMutation(
    async (d) => {
      if (!editando) return (await api.post('/auth/paquetes/', d)).data
      // Solo presentación: se edita el mismo plan, sin versionar.
      if (camposDeProductoModificados(plan, d).length === 0) {
        return (await api.patch(`/auth/paquetes/${plan.id}/`, d)).data
      }
      // Cambió lo que se vende: versión nueva y la anterior sale de la venta.
      return (await api.post(`/auth/paquetes/${plan.id}/nueva_version/`, d)).data
    },
    {
      onSuccess: (res) => {
        if (!editando) toast.success('Plan creado')
        else if (res?.paquete) {
          const n = res.licencias_en_el_anterior
          // El nombre ya trae el sufijo de versión; repetirlo se lee como un error.
          const yaDiceVersion = new RegExp(`v${res.paquete.version}$`).test(res.paquete.nombre)
          toast.success(
            `Publicado ${res.paquete.nombre}` +
            (yaDiceVersion ? '' : ` (v${res.paquete.version})`) +
            `. El plan anterior salió de la venta` +
            (n ? `; ${n} licencia${n === 1 ? '' : 's'} sigue${n === 1 ? '' : 'n'} con él.` : '.')
          )
        } else toast.success('Plan actualizado')
        onSuccess()
      },
      onError: (e) => {
        setErrores(e.response?.data || {})
        toast.error(e.response?.data?.detail || 'No se pudo guardar el plan')
      },
    }
  )

  const enviar = (e) => {
    e.preventDefault()
    // Si cambió la definición del producto, se avisa antes de publicar nada.
    if (cambiosDeProducto.length > 0 && !confirmando) {
      setConfirmando(cambiosDeProducto)
      return
    }
    setConfirmando(null)
    crear.mutate(datos)
  }

  const cambiar = (e) => setDatos(p => ({ ...p, [e.target.name]: e.target.value }))

  return (
    <Modal show onHide={onHide} size="lg">
      <Form onSubmit={enviar}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editando
              ? `Plan: ${plan.nombre} (v${plan.version})`
              : 'Nuevo plan comercial'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmando && (
            <Alert variant="warning">
              <Alert.Heading className="h6">Esto cambia lo que se vende</Alert.Heading>
              <p className="mb-2">
                Estás cambiando{' '}
                <strong>{describirCambios(confirmando)}</strong>.
                Esos campos definen el producto, así que no se sobreescriben: se publicará{' '}
                <strong>la versión {(plan.version || 1) + 1}</strong> y la actual saldrá de la
                venta.
              </p>
              <p className="mb-0 small">
                Las <strong>{plan.licencias_activas || 0} licencias</strong> que ya tienen este
                plan <strong>no cambian</strong>: conservan las condiciones con que se vendieron.
                Moverlas al plan nuevo es una decisión aparte.
              </p>
            </Alert>
          )}
          {editando && !confirmando && cambiosDeProducto.length > 0 && (
            <Alert variant="light" className="border small py-2">
              Al guardar se publicará una versión nueva, porque cambiaste{' '}
              {describirCambios(cambiosDeProducto)}.
            </Alert>
          )}
          <Row>
            <Col md={12} className="mb-3">
              <Form.Label>Nombre del plan *</Form.Label>
              <Form.Control name="nombre" value={datos.nombre} onChange={cambiar}
                            placeholder="Facturación 5 RFC" isInvalid={!!errores.nombre} />
              <Form.Control.Feedback type="invalid">{errores.nombre}</Form.Control.Feedback>
            </Col>
            <Col md={12} className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control as="textarea" rows={2} name="descripcion"
                            value={datos.descripcion || ''} onChange={cambiar}
                            placeholder="Para qué sirve este plan y a quién se le vende." />
              <Form.Text className="text-muted">
                No afecta lo que se vende: se puede cambiar sin publicar versión nueva.
              </Form.Text>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check type="switch" id="plan-activo" label="A la venta"
                          checked={Boolean(datos.activo)}
                          onChange={() => setDatos((d) => ({ ...d, activo: !d.activo }))} />
              <Form.Text className="text-muted">
                Apagado, el plan deja de ofrecerse pero conserva su historial.
              </Form.Text>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check type="switch" id="plan-interno" label="Paquete interno"
                          checked={Boolean(datos.es_interno)}
                          onChange={() => setDatos((d) => ({ ...d, es_interno: !d.es_interno }))} />
              <Form.Text className="text-muted">
                Un paquete interno <strong>no consume timbres de la bolsa</strong>. Es para las
                licencias propias, no para vender.
              </Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Capacidad (RFC) *</Form.Label>
              <Form.Control type="number" min={1} name="capacidad_rfc"
                            value={datos.capacidad_rfc} onChange={cambiar}
                            isInvalid={!!errores.capacidad_rfc} />
              <Form.Text className="text-muted">Contribuyentes a la vez.</Form.Text>
              <Form.Control.Feedback type="invalid">{errores.capacidad_rfc}</Form.Control.Feedback>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Reemplazos por vigencia</Form.Label>
              <Form.Control type="number" min={0} name="reemplazos_incluidos"
                            value={datos.reemplazos_incluidos} onChange={cambiar} />
              <Form.Text className="text-muted">0 = sin rotación.</Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Timbres incluidos</Form.Label>
              <Form.Control type="number" min={0} name="timbres_incluidos"
                            value={datos.timbres_incluidos} onChange={cambiar} />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Precio</Form.Label>
              <Form.Control name="precio" value={datos.precio} onChange={cambiar} />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Usuarios permitidos</Form.Label>
              <Form.Control type="number" min={1} name="max_usuarios"
                            value={datos.max_usuarios} onChange={cambiar} />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Vigencia (días)</Form.Label>
              <Form.Control type="number" min={0} name="vigencia_dias"
                            value={datos.vigencia_dias} onChange={cambiar} />
              <Form.Text className="text-muted">0 = sin vencimiento.</Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Días de retención</Form.Label>
              <Form.Control type="number" min={0} name="dias_retencion"
                            value={datos.dias_retencion ?? 0} onChange={cambiar} />
              <Form.Text className="text-muted">
                Cuánto queda ocupado el lugar de un RFC tras darlo de baja.
              </Form.Text>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Sustituciones por lugar</Form.Label>
              <Form.Control type="number" min={0} name="max_sustituciones_slot"
                            value={datos.max_sustituciones_slot ?? 0} onChange={cambiar} />
              <Form.Text className="text-muted">
                Cuántas veces se puede reemplazar el RFC de un mismo lugar.
              </Form.Text>
            </Col>
          </Row>

          <hr />
          <Form.Label>Módulos que incluye *</Form.Label>
          <p className="text-muted small mb-2">
            Determina qué partes del sistema verá el contador. El tablero y los contribuyentes
            van siempre: sin ellos no hay nada que facturar.
          </p>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {(catalogo?.base || []).map((m) => (
              <Badge bg="secondary" key={m}>{m} (siempre)</Badge>
            ))}
          </div>
          <Row>
            {(catalogo?.vendibles || []).map((m) => (
              <Col md={4} key={m.clave} className="mb-2">
                <Form.Check type="checkbox" id={`mod-${m.clave}`} label={m.etiqueta}
                            checked={datos.modulos.includes(m.clave)}
                            onChange={() => alternarModulo(m.clave)} />
              </Col>
            ))}
          </Row>
          {datos.modulos.length === 0 && (
            <Alert variant="warning" className="mt-2 mb-0 py-2 small">
              Sin ningún módulo, el contador sólo podrá administrar contribuyentes: no podrá
              facturar. Si es a propósito, adelante.
            </Alert>
          )}

          <hr />
          <Form.Check type="checkbox" id="permite-bloques" className="mb-2"
                      label="Admite bloques adicionales"
                      checked={datos.permite_bloques}
                      onChange={() => setDatos((d) => ({ ...d,
                                                         permite_bloques: !d.permite_bloques }))} />
          <p className="text-muted small">
            Deja que el contador que llegó a su tope <strong>amplíe sin cambiar de plan</strong>:
            compra capacidad en bloques. Se vende desde <em>Ampliar</em>, en la tabla de licencias.
          </p>
          {datos.permite_bloques && (
            <Row>
              <Col md={4} className="mb-3">
                <Form.Label>RFC por bloque</Form.Label>
                <Form.Control type="number" min={0} name="tamano_bloque"
                              value={datos.tamano_bloque} onChange={cambiar} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Timbres por bloque</Form.Label>
                <Form.Control type="number" min={0} name="timbres_bloque"
                              value={datos.timbres_bloque} onChange={cambiar} />
                <Form.Text className="text-muted">0 = sólo capacidad.</Form.Text>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Precio por bloque</Form.Label>
                <Form.Control name="precio_bloque" value={datos.precio_bloque}
                              onChange={cambiar} />
              </Col>
            </Row>
          )}

          {errores.modulos && <Alert variant="danger" className="mt-2">{String(errores.modulos)}</Alert>}
          {errores.detail && <Alert variant="danger">{errores.detail}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          {confirmando && (
            <Button variant="outline-secondary" onClick={() => setConfirmando(null)}>
              Revisar
            </Button>
          )}
          <Button type="submit" variant={confirmando ? 'warning' : 'primary'}
                  disabled={crear.isLoading}>
            {crear.isLoading
              ? 'Guardando…'
              : confirmando
                ? `Publicar versión ${(plan.version || 1) + 1}`
                : editando ? 'Guardar' : 'Crear plan'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

function FormAsignar({ paquetes, onHide, onSuccess }) {
  const [contador, setContador] = useState('')
  const [paquete, setPaquete] = useState('')
  const [errores, setErrores] = useState({})

  const { data: contadores = [] } = useQuery(
    'contadores-sin-licencia',
    async () => (await api.get('/auth/licencias/contadores_sin_licencia/')).data
  )
  const { data: simulacion } = useQuery(
    ['simular-bolsa', paquete],
    async () => (await api.get('/auth/bolsa-timbres/simular/', { params: { paquete } })).data,
    { enabled: !!paquete }
  )

  const asignar = useMutation(
    async () => (await api.post('/auth/licencias/', {
      contador, paquete_origen: paquete, estado: 'activa',
    })).data,
    {
      onSuccess: () => { toast.success('Licencia asignada'); onSuccess() },
      onError: (e) => {
        setErrores(e.response?.data || {})
        toast.error('No se pudo asignar la licencia')
      },
    }
  )

  const bloqueado = simulacion && !simulacion.permite

  return (
    <Modal show onHide={onHide}>
      <Form onSubmit={(e) => { e.preventDefault(); asignar.mutate() }}>
        <Modal.Header closeButton><Modal.Title>Asignar plan a un contador</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Contador *</Form.Label>
            <Form.Select name="contador" value={contador}
                         onChange={(e) => setContador(e.target.value)}>
              <option value="">Seleccionar…</option>
              {contadores.map(c => (
                <option key={c.id} value={c.id}>{c.username}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Sólo aparecen los contadores que todavía no tienen licencia.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Plan *</Form.Label>
            <Form.Select name="paquete_origen" value={paquete}
                         onChange={(e) => setPaquete(e.target.value)}>
              <option value="">Seleccionar…</option>
              {paquetes.filter(p => p.activo && !p.es_interno).map(p => (
                <option key={p.id} value={p.id}>
                  {p.nombre} — {p.capacidad_texto}, {p.timbres_incluidos} timbres
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {simulacion && (
            <Alert variant={simulacion.permite ? 'success' : 'danger'} className="mb-0">
              {simulacion.motivo}
              {!simulacion.permite && simulacion.faltan > 0 && (
                <div className="mt-2">
                  <strong>Faltan {simulacion.faltan} timbres.</strong> Cómpralos y regístralos
                  con «Registrar compra de timbres».
                </div>
              )}
            </Alert>
          )}
          {errores.bolsa && <Alert variant="danger" className="mt-3 mb-0">{errores.bolsa}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={!contador || !paquete || bloqueado || asignar.isLoading}>
            {asignar.isLoading ? 'Asignando…' : 'Asignar plan'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

function FormSaldo({ bolsa, onHide, onSuccess }) {
  const [saldo, setSaldo] = useState('')
  const [nota, setNota] = useState('')
  const hoy = new Date().toISOString().slice(0, 10)
  const [fecha, setFecha] = useState(hoy)

  const registrar = useMutation(
    async () => (await api.post('/auth/bolsa-timbres/', {
      saldo: parseInt(saldo, 10), fecha_corte: fecha, nota,
    })).data,
    {
      onSuccess: () => { toast.success('Saldo registrado'); onSuccess() },
      onError: () => toast.error('No se pudo registrar el saldo'),
    }
  )

  return (
    <Modal show onHide={onHide}>
      <Form onSubmit={(e) => { e.preventDefault(); registrar.mutate() }}>
        <Modal.Header closeButton><Modal.Title>Registrar compra de timbres</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            El PAC informa el saldo cada vez que se timbra. Esto es para el momento de comprar:
            registra aquí el saldo nuevo y el sistema lo usará hasta que el PAC confirme el suyo
            en el siguiente timbrado.
          </p>
          {bolsa?.pac?.disponible && (
            <Alert variant="light" className="py-2">
              Último dato del PAC: <strong>{bolsa.pac.saldo}</strong> timbres al {bolsa.pac.fecha_corte}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Timbres disponibles tras la compra *</Form.Label>
            <Form.Control type="number" min={0} name="saldo" value={saldo}
                          onChange={(e) => setSaldo(e.target.value)} placeholder="1500" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Fecha del corte</Form.Label>
            <Form.Control type="date" name="fecha_corte" value={fecha}
                          onChange={(e) => setFecha(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Nota</Form.Label>
            <Form.Control name="nota" value={nota} onChange={(e) => setNota(e.target.value)}
                          placeholder="Compra de 1,000 timbres, factura A-123" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancelar</Button>
          <Button type="submit" disabled={!saldo || registrar.isLoading}>
            {registrar.isLoading ? 'Registrando…' : 'Registrar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
